import type { GradeValue } from '@shkola/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, todayIso } from './dates';
import { type Db, must, useDb } from './db';
import { type AbsenceMark, type GradeRow, gradeValue } from './journal';
import type { LessonFormat, LessonStatus } from './schedule';

export interface StudentGrade {
  id: string;
  courseId: string;
  subject: string;
  kind: GradeRow['kind'];
  value: GradeValue | null;
  special: GradeRow['special'];
  date: string;
  title: string | null;
  topic: string | null;
  comment: string | null;
  /** the lesson it was given at, when it was given at a lesson */
  lessonId: string | null;
}

export interface StudentAbsence {
  lessonId: string;
  courseId: string;
  subject: string;
  date: string;
  mark: AbsenceMark;
}

export interface StudentFormative {
  id: string;
  subject: string;
  date: string;
  sticker: string | null;
  comment: string | null;
}

export interface StudentRecord {
  grades: StudentGrade[];
  absences: StudentAbsence[];
  formative: StudentFormative[];
}

const SUBJECT =
  'course:courses!grades_school_id_course_id_fkey(subject:subjects!courses_school_id_subject_id_fkey(name))';

export async function fetchStudentRecord(
  db: Db,
  studentId: string,
  termId: string,
): Promise<StudentRecord> {
  const [grades, absences, formative] = await Promise.all([
    db
      .from('grades')
      .select(
        `id, course_id, kind, scale, points, level, verbal, passed, special, title, comment, graded_on,
        lesson:lessons!grades_lesson_id_course_id_fkey(id, topic), ${SUBJECT}`,
      )
      .eq('student_member_id', studentId)
      .eq('term_id', termId)
      .order('graded_on', { ascending: false }),
    db
      .from('attendance')
      .select(
        `lesson_id, course_id, mark,
        lesson:lessons!attendance_lesson_id_course_id_fkey(date, term_id),
        course:courses!attendance_school_id_course_id_fkey(subject:subjects!courses_school_id_subject_id_fkey(name))`,
      )
      .eq('student_member_id', studentId),
    db
      .from('formative_marks')
      .select(
        `id, sticker, comment,
        lesson:lessons!formative_marks_lesson_id_course_id_fkey(date, term_id),
        course:courses!formative_marks_school_id_course_id_fkey(subject:subjects!courses_school_id_subject_id_fkey(name))`,
      )
      .eq('student_member_id', studentId),
  ]);
  return {
    grades: must(grades).map((g) => ({
      id: g.id,
      courseId: g.course_id,
      subject: g.course.subject.name,
      kind: g.kind,
      value: gradeValue(g),
      special: g.special,
      date: g.graded_on,
      title: g.title,
      topic: g.lesson?.topic ?? null,
      comment: g.comment,
      lessonId: g.lesson?.id ?? null,
    })),
    absences: must(absences)
      .filter((a) => a.lesson.term_id === termId)
      .map((a) => ({
        lessonId: a.lesson_id,
        courseId: a.course_id,
        subject: a.course.subject.name,
        date: a.lesson.date,
        mark: a.mark,
      }))
      .sort((a, b) => b.date.localeCompare(a.date)),
    formative: must(formative)
      .filter((f) => f.lesson.term_id === termId)
      .map((f) => ({
        id: f.id,
        subject: f.course.subject.name,
        date: f.lesson.date,
        sticker: f.sticker,
        comment: f.comment,
      }))
      .sort((a, b) => b.date.localeCompare(a.date)),
  };
}

export function useStudentRecord(studentId: string, termId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['student-record', studentId, termId],
    enabled: termId !== undefined,
    queryFn: () => fetchStudentRecord(db, studentId, termId as string),
  });
}

/**
 * School days in a row the student did not miss, counting back from the latest finished one.
 * Only a mark «absent» breaks it: an air-raid alarm or a technical failure is not the student's
 * choice.
 */
export function presenceStreak(
  schoolDays: ReadonlyArray<string>,
  absentDays: ReadonlySet<string>,
): number {
  let streak = 0;
  for (const day of [...new Set(schoolDays)].sort().reverse()) {
    if (absentDays.has(day)) break;
    streak += 1;
  }
  return streak;
}

/** The student's presence streak over the last ten weeks (the sticker on their home screen). */
export function usePresenceStreak(studentId: string) {
  const db = useDb();
  const today = todayIso();
  const from = addDays(today, -70);
  return useQuery({
    queryKey: ['presence-streak', studentId, today],
    queryFn: async () => {
      const courseIds = must(
        await db.from('course_students').select('course_id').eq('student_member_id', studentId),
      ).map((r) => r.course_id);
      if (courseIds.length === 0) return 0;
      const [lessons, absences] = await Promise.all([
        db
          .from('lessons')
          .select('date, ends_at')
          .in('course_id', courseIds)
          .eq('status', 'scheduled')
          .gte('date', from)
          .lte('date', today),
        db
          .from('attendance')
          .select('lesson:lessons!attendance_lesson_id_course_id_fkey!inner(date)')
          .eq('student_member_id', studentId)
          .eq('mark', 'absent')
          .gte('lesson.date', from),
      ]);
      const now = Date.now();
      const days = must(lessons)
        .filter((l) => Date.parse(l.ends_at) <= now)
        .map((l) => l.date);
      return presenceStreak(days, new Set(must(absences).map((a) => a.lesson.date)));
    },
  });
}

/**
 * How good a grade is, in four steps for colour: 1–3 · 4–6 · 7–9 · 10–12 on the 12-point scale,
 * the four levels and the four verbal steps of the primary school, fail / pass.
 */
export function gradeLevel(value: GradeValue | null): 0 | 1 | 2 | 3 {
  if (!value) return 1;
  switch (value.scale) {
    case 'points12':
      return Math.min(3, Math.max(0, Math.ceil(value.points / 3) - 1)) as 0 | 1 | 2 | 3;
    case 'levels4':
      return Math.max(0, ['П', 'С', 'Д', 'В'].indexOf(value.level)) as 0 | 1 | 2 | 3;
    case 'verbal':
      return Math.max(
        0,
        ['needs_help', 'with_teacher_help', 'notable_progress', 'significant_success'].indexOf(
          value.verbal,
        ),
      ) as 0 | 1 | 2 | 3;
    case 'pass_fail':
      return value.passed ? 3 : 0;
  }
}

/** Average of 12-point grades (current, tests, thematic), one decimal; null when there are none. */
export function pointsAverage(grades: ReadonlyArray<StudentGrade>): number | null {
  const points = grades
    .filter((g) => g.kind !== 'semester' && g.kind !== 'year')
    .map((g) => (g.value?.scale === 'points12' ? g.value.points : null))
    .filter((p): p is number => p !== null);
  if (points.length === 0) return null;
  return Math.round((points.reduce((s, p) => s + p, 0) / points.length) * 10) / 10;
}

/** Administration: format, cancellation and substitutes of a lesson. */
export function useLessonAdminActions() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: {
      lessonId: string;
      format?: LessonFormat;
      status?: LessonStatus;
      substituteMemberId?: string | null;
    }) =>
      must(
        await db
          .from('lessons')
          .update({
            ...(a.format && { format: a.format }),
            ...(a.status && { status: a.status }),
            ...(a.substituteMemberId !== undefined && {
              substitute_member_id: a.substituteMemberId,
            }),
          })
          .eq('id', a.lessonId),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['lessons'] }),
  });
}

/** Staff of a school who teach — candidates for a substitution. */
export function useTeachers(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['teachers', schoolId],
    staleTime: 10 * 60_000,
    queryFn: async () =>
      must(
        await db
          .from('staff_roles')
          .select(
            'member:school_members!staff_roles_school_id_member_id_fkey(id, first_name, last_name, middle_name)',
          )
          .eq('school_id', schoolId)
          .eq('role', 'teacher'),
      )
        .map((r) => r.member)
        .sort((a, b) => a.last_name.localeCompare(b.last_name, 'uk')),
  });
}

/** Absences recorded for lessons on a date, across the school (administration dashboard). */
export function useAbsencesOn(schoolId: string, date: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['absences-on', schoolId, date],
    queryFn: async () =>
      must(
        await db
          .from('attendance')
          .select(
            'student_member_id, mark, lesson:lessons!attendance_lesson_id_course_id_fkey!inner(date)',
          )
          .eq('school_id', schoolId)
          .eq('lesson.date', date),
      ),
  });
}
